// Одна плашка о связи с сервером на всё приложение (Алексей 29.10 #3224: «централизованный хендлер, а
// не везде свой обработчик»). Стоит в каркасе над страницей и говорит за все секции сразу:
//   • вы не в сети / сервер не отвечает / сервер обновляется (только по файлу монитора деплоя) /
//     снова на связи;
//   • сервер жив, но часть запросов упала (таймаут, 5xx) — «часть данных не загрузилась», повтор сам
//     с нарастающей паузой (lib/serverHealth) и кнопкой.
// Пока плашка видна, ServerStatusNotice в секциях молчит (тихая заглушка), так что текст один.
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CloudOff, Loader2, WifiOff, CheckCircle2 } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { useServerHealth, clearFailures } from '@/lib/serverHealth';
import { viewOf, progressOf, SUPPORT_TG } from '@/components/common/ServerStatusNotice';
import { robotCoversPage } from './ServerDownOverlay';

/** «Проверяем связь» дольше этого — показать; короче — не мигать плашкой на каждом сбое. */
const CHECKING_SHOW_MS = 1500;

export function ServerStatusBar() {
  const { t } = useT();
  const health = useServerHealth();
  const location = useLocation();
  const [now, setNow] = useState(() => Date.now());

  // Сбои прошлой страницы к новой не относятся.
  useEffect(() => { clearFailures(); }, [location.pathname]);

  const ticking = health.kind !== 'ok';
  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [ticking]);

  // Кнопки «Повторить» нет (Алексей 31.10 #3462): проверка идёт сама каждые несколько секунд.

  let icon: React.ReactNode;
  let title: string;
  let hint: string | null = null;
  let contact = false;
  let progress: number | null = null;
  let tone: 'muted' | 'warn' | 'ok' = 'muted';

  // «Часть данных не загрузилась» не показываем (Алексей 03.11 #3566: «это надо ваще убрать»): повтор идёт
  // сам (lib/serverHealth), секции на это время — тихие рамки без текста.
  if (health.kind === 'ok') {
    return null;
  } else {
    // Сервер лежит / нет сети — говорит робот поверх страницы (ServerDownOverlay, #3274), не плашка.
    if (robotCoversPage(health.kind)) return null;
    if (health.kind === 'checking' && now - health.since < CHECKING_SHOW_MS) return null;
    const view = viewOf(health, null, now);
    switch (view) {
      case 'offline':
        icon = <WifiOff className="size-4" />;
        title = t('serverStatus.offline.title');
        hint = t('serverStatus.offline.hint');
        break;
      case 'checking':
        icon = <Loader2 className="size-4 animate-spin" />;
        title = t('serverStatus.checking.title');
        break;
      case 'updating':
        icon = <Loader2 className="size-4 animate-spin" />;
        title = t('serverStatus.updating.title');
        progress = progressOf(health, now);
        hint = t(progress > 0.9 ? 'serverStatus.updating.slow' : 'serverStatus.updating.hint');
        break;
      case 'noResponse':
        icon = <CloudOff className="size-4" />;
        title = t('serverStatus.noResponse.title');
        hint = t('serverStatus.noResponse.hint');
        tone = 'warn';
        break;
      case 'back':
        icon = <CheckCircle2 className="size-4" />;
        title = t('serverStatus.back.title');
        progress = 1;
        tone = 'ok';
        break;
      default:
        icon = <CloudOff className="size-4" />;
        title = t('dataError.title');
        hint = t('dataError.retryHint');
        contact = true;
        tone = 'warn';
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm',
        tone === 'warn' && 'border-warning/30 bg-warning/10 text-foreground',
        tone === 'ok' && 'border-border bg-muted text-foreground',
        tone === 'muted' && 'border-border bg-muted text-foreground',
      )}
    >
      <span className={cn('flex shrink-0 items-center', tone === 'warn' ? 'text-warning' : 'text-muted-foreground')}>{icon}</span>
      <span className="font-medium">{title}</span>
      {hint && <span className="min-w-0 text-muted-foreground">{hint}</span>}
      {contact && (
        <span className="text-muted-foreground">
          {t('dataError.contactPrefix')}{' '}
          <a href={`https://t.me/${SUPPORT_TG}`} target="_blank" rel="noreferrer" className="font-medium text-primary underline-offset-4 hover:underline">
            @{SUPPORT_TG}
          </a>
        </span>
      )}
      {progress != null && (
        <span className="h-1.5 w-28 overflow-hidden rounded-full bg-background" aria-hidden>
          <span className="block h-full rounded-full bg-primary transition-[width] duration-500 ease-linear" style={{ width: `${Math.round(progress * 100)}%` }} />
        </span>
      )}
    </div>
  );
}

export default ServerStatusBar;
